import { useMemo } from 'react';
import { groupOfCategory, usePartsRun } from './partsRun';
import { usePartCategories } from './partCategories';
import { useCategories } from './catalogCategories';
import { useListingCrops } from './listingCropsRun';
import { selectedCrops, useListingSelection, viewsOf } from './listingSelectRun';
import { useSubmodels } from './listingStore';

// THE PARTS UP CLOSE, for a whole product's 3D model — Phase 2's selected listing crops, sent
// after the product's photographs so the model sees each part's shape (see
// openai_blender.crops_block, where the argument is written).
//
// COMPONENTS ONLY, TWO EACH AT MOST: the top two the selection ranked, which it already made
// come from different sides. Hardware — a wood button, a cap — is a picture that changes little
// about a model and costs tokens on every pass.
//
// The photograph and its box (padding included), never pixels: the server fetches and cuts
// them, and the run records them so its reference row and its prompt panel can show them.
export type PartCrop = { part: string; view: string; url: string; bbox: [number, number, number, number] };

const PER_PART = 2;

export function useProductPartCrops(docId: string): PartCrop[] {
  const { items } = usePartsRun(docId);
  const categories = usePartCategories(items);
  // THE ONTOLOGY, subscribed: which group a category is in is only known once it has loaded, and
  // before that every category reads as a component — which sent a wood button's close-ups.
  const ontology = useCategories();
  const { byPart } = useListingCrops(docId);
  const { selected } = useListingSelection(docId);
  // Subscribed so a re-read of the photographs' sides reaches the captions.
  const submodels = useSubmodels(docId);
  return useMemo(() => {
    const views = viewsOf(docId);
    return items.flatMap((p, i) => {
      if (p.tool || !ontology.length || groupOfCategory(categories[i], ontology) !== 'Components') return [];
      return selectedCrops(byPart[i] ?? [], selected[i]).slice(0, PER_PART).map((c) => ({
        part: p.nameEn || p.name || '',
        view: views.get(c.url) ?? '',
        url: c.url,
        bbox: c.bbox,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, items, categories, ontology, byPart, selected, submodels]);
}

// THE CLOSE-UPS A RUN WAS SENT, off the run itself — [] for a run made without them.
export const partCropsOf = (job: { params?: Record<string, unknown> | null } | null | undefined): PartCrop[] =>
  (Array.isArray(job?.params?.partCrops) ? job.params.partCrops as PartCrop[] : []);
